/**
 * src/components/pdf/2024/sections/ResponsiblePracticesPDF.js
 *
 * Responsible Practices section — section cover + content pages.
 *
 * Named exports: ResponsiblePracticesPage, ResponsiblePracticesSection
 * Default export: standalone Document (used by the generate script)
 */

import React from 'react';
import { Document, Text, Image, View, Link } from '@react-pdf/renderer';
import { styles, COLORS, registerFonts } from '../styles';
import PageTemplate from '../PageTemplate';
import {
  H1, H2, H3, H4, Body, Section,
  BulletList, SectionCover,
  PageOffsetContext,
} from '../shared';

import respPracticesHero from '../../../ui/corporateresponsibility/impact/2024-impact-report/responsible-practices/resp_practices_cover_16x9.jpg';
import riseImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/responsible-practices/rise_comp.jpg';
import scorecardImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/responsible-practices/responsible-practices_scorecard-ratings.png';
import assessmentsImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/responsible-practices/responsible-practices_assessments-at-partners.png';

registerFonts();

// ---------------------------------------------------------------------------
// Responsible Practices content pages
// ---------------------------------------------------------------------------
export function ResponsiblePracticesPage() {
  return (
    <>
      <PageTemplate sectionTitle="Responsible Practices" accentColor={COLORS.orange}>
        <Section>
          <H1 color={COLORS.orange}>Responsible Practices</H1>

          <H2 color={COLORS.orange}>Ethics & Compliance</H2>
          <Text style={styles.body}>
            All Columbia Sportswear Company ("CSC") employees, Board members, and certain partners
            conducting business on our behalf are responsible for understanding and complying with
            our <Link href="https://d1io3yog0oux5.cloudfront.net/_06b9c61d71a960f03e9ac70564a02500/columbia/db/654/5320/file/Corporate_Code_of_Business-Conduct_and_Ethics_English_.pdf">Code of Business Conduct and Ethics</Link>, along with all related policies. This
            values-based Code guides our business, meaning that, in addition to complying with the
            law, we:
          </Text>
          <BulletList
            items={[
              'Commit to ethical conduct',
              'Encourage and support a speak-up culture',
              'Treat one another with dignity and respect',
              'Conduct business honestly and compete fairly in the marketplace',
              'Protect and properly use company information',
              'Engage third-party partners who share our values',
            ]}
          />
          <Body>
            Our Ethics and Compliance program is supported by our Corporate Compliance Committee,
            which champions a culture of ethics, compliance, and alignment with our Code of
            Business Conduct and Ethics. The committee is composed of senior leaders from Human
            Resources, Legal, Finance, Internal Audit, and Business teams. With support from the
            Compliance Committee and the Ethics and Compliance Program, our Audit Committee of the
            Board oversees the organizational structures, policies, and procedures that promote an
            ethical, compliant culture at CSC.
          </Body>
          <Body>
            We provide a confidential compliance phone line and a web-based reporting platform for
            employees and other stakeholders to report concerns. Both channels follow CSC's
            confidentiality protocols and allow anonymous reporting where permitted by local law.
            All reports submitted through these channels related to compliance are shared with the
            Chair of the Audit Committee.
          </Body>
          <Body>
            Each July, we recognize Ethics & Compliance Month with an internal employee campaign
            centered around our value of "Doing the Right Thing." In 2024, the campaign highlighted
            the Compliance Line and promoted a culture of speaking up.
          </Body>
        </Section>

        <Section>
          <H2 color={COLORS.orange}>Traceability</H2>
          <Text style={styles.body}>
            CSC has long been committed to transparency and traceability. Since 2013, we have
            updated our <Link href="https://www.columbiasportswearcompany.com/corporate-responsibility-group/responsible-practices/supply-chain">Supply Chain Transparency Map</Link> semiannually, publicly sharing information
            about our Tier 1 and Tier 2 manufacturing partners. This interactive map allows users
            to trace the global production locations of our apparel, footwear, and other products.
          </Text>
          <Body>
            In addition to our live Supply Chain Transparency Map, we continue to invest in proven
            strategies to reduce upstream supply chain risks and disruptions. This includes adopting
            new technology platforms that enable our supply chain partners to provide key
            information about the upstream facilities and processes involved in creating our
            products. We have also implemented risk-based testing to verify material origins and
            standardized our chain of custody documentation collection and validation processes.
            These initiatives enhance the granularity, completeness, and accuracy of our supply
            chain maps.
          </Body>
        </Section>
      </PageTemplate>

      <PageTemplate sectionTitle="Responsible Practices" accentColor={COLORS.orange}>
        <Section>
          <H2 color={COLORS.orange}>Social Responsibility In Our Supply Chain</H2>

          <View>
            <H3>RISE</H3>
            <View style={{ flexDirection: 'row', gap: 24 }}>
              <View style={{ flex: 1 }}>
                <Text style={styles.body}>
                  CSC's partnership with RISE (Reimagining Industry to Support Equality) has had a
                  positive impact on thousands of workers, predominantly women-identifying, in the global
                  garment, footwear, and home textiles supply chains. With RISE's focus on collaborative
                  industry action to improve equality and create resilient futures, CSC has helped fund
                  49 RISE workplace programs across six countries. Since 2008, over 106,000 workers have
                  benefitted from the education and resources provided through our ongoing partnership,
                  with over 84,000 participants identifying as women. RISE programs focus on financial
                  health, preventing and remediating violence and harassment in the workplace, skills
                  development, and digital literacy and access. RISE's core strategies include
                  strengthening program capacity, fostering collaborative market transformation, and
                  influencing public policy. In 2024, RISE, along with its industry supporters, reached
                  over 425 workplaces, impacting over 375,000 workers, over 65% of whom identify as
                  women. To learn more about RISE programs, visit the{" "}
                  <Link href="https://riseequal.org/">RISE website</Link>,{" "}
                  <Link href="https://www.linkedin.com/company/reimaginingindustrytosupportequality/posts/?feedView=all">LinkedIn account</Link> and{" "}
                  <Link href="https://www.youtube.com/@RISE_Equal">YouTube Channel</Link>.
                </Text>
              </View>
              <View style={{ flex: 1 }}>
                <Image src={riseImg} style={{ width: '100%' }} />
              </View>
            </View>
          </View>

            </Section>
          </PageTemplate>

          <PageTemplate sectionTitle="Responsible Practices" accentColor={COLORS.orange}>
           <Section>
          <H3>Manufacturing Scorecard Program</H3>
          <Body>
            Our CSC Supplier Code of Conduct, also known as our Standards of Manufacturing
            Practices ("SMP"), outlines the requirements for the production of apparel, footwear,
            equipment, and accessories. The SMP aligns with the standards set by the International
            Labour Organization ("ILO"), the Fair Labor Association ("FLA"), and industry best
            practices, and includes strict prohibitions on forced labor throughout our supply chain.
          </Body>
          <Body>
            In 2024, we worked directly with 330 finished good vendors, 87% of whom completed at
            least one audit or assessment during the year. To evaluate Tier 1 vendor compliance
            with our SMP and local labor laws, we use a variety of methods, including:
          </Body>
          <BulletList
            items={[
              "Assessments conducted by the ILO Better Work program",
              "Verified assessments using the Social and Labor Convergence Program's (\"SLCP\") Converged Assessment Framework",
              "Unannounced audits by our internal specialists or designated third-party firms",
              "Audits aligned with other recognized standards",
            ]}
          />
          <Body>
            We use the results of these audits and assessments to generate performance scorecards,
            which are updated monthly. When issues are identified, corrective action plans are
            promptly implemented to address and prevent recurrence.
          </Body>
          </Section>
          </PageTemplate>

          <PageTemplate sectionTitle="Responsible Practices" accentColor={COLORS.orange}>
           <Section>
          <View>
            <Text style={{ fontSize: 16, lineHeight: 1.4, marginVertical: 12, fontWeight: 700}}>
              2018–2024 CSC Tier 1 Manufacturing Partner Social Responsibility Scorecard Ratings{"\n"}
              Social Responsibility Scorecard Ratings (Percent)
            </Text>
            <Image src={scorecardImg} style={[styles.chartImage, {width: '70%'}]} />
          </View>
        </Section>

        <Section>
          <H3>Audits & Assessments</H3>
          <Body>
            Ensuring safe and proper working conditions across our supply chain is a core
            expectation of business partners. We support these partners with a dedicated,
            international team of in-house experts who provide on-site guidance and assessments for
            continuous improvement. Our social responsibility program focuses on ensuring that our
            supply chain operates in accordance with our Standards of Manufacturing Practices
            ("SMP") and our Code of Business Conduct and Ethics. We remain committed to supporting
            our supply chain partners through resource capacity building, worker empowerment
            programs, and corrective remediation plans.
          </Body>

            </Section>
          </PageTemplate>

          <PageTemplate sectionTitle="Responsible Practices" accentColor={COLORS.orange}>
           <Section>
          <H4>Better Work Program</H4>
          <Body>
            The United Nations' International Labour Organization ("ILO") and the International
            Finance Corporation ("IFC"), a member of the World Bank Group, partner to support the
            apparel sector through the Better Work program. This collaborative initiative brings
            together the apparel and textile industry to improve working conditions, uphold workers'
            labor rights, and enhance the global industry's competitiveness. CSC actively engages
            with and encourages our supplier partners to join the Better Work program, reinforcing
            labor standards and national legislative policies. Contributing to the improvement of
            workers' compensation and labor conditions is a key aspect of CSC's core value of
            "doing the right thing." In 2024, 81 of our finished goods manufacturing partner
            facilities were registered with the Better Work program.
          </Body>

          <H4>Social Labor & Convergence Program (SLCP)</H4>
          <Body>
            Launched in 2015, the Social & Labor Convergence Program ("SLCP") was developed
            through collaboration within the apparel and footwear industry. The program's primary
            focus is to monitor and improve working conditions across global supply chains by
            standardizing manufacturing audits through the Converged Assessment Framework ("CAF").
            CAF delivers reliable audit data that allows brands to track and engage product vendors
            efficiently to foster continuous improvement in working conditions. Given the highly
            collaborative nature of the apparel and footwear industry, these standardized
            assessments allow industry stakeholders sourcing from the same suppliers to collectively
            support vendors in improving workplace conditions. SLCP streamlines data collection,
            verification, and sharing, helping brands enhance their social responsibility programs.
            In 2024, 215 manufacturing partners (Tier 1 and Tier 2) completed the SLCP
            self-assessment and verification process. These assessments contribute to our vendor
            scorecards and guide ongoing improvement efforts.
          </Body>
          </Section>
          </PageTemplate>

          <PageTemplate sectionTitle="Responsible Practices" accentColor={COLORS.orange}>
           <Section>
            <View>
              <Text style={{ fontSize: 16, lineHeight: 1.4, marginVertical: 12, fontWeight: 700}}>
                Number of Industry Assessments Performed at our Manufacturing Partners' Facilities
              </Text>
              <Image src={assessmentsImg} style={[styles.chartImage, {width: '70%'}]} />
            </View>
        </Section>
      </PageTemplate>
    </>
  );
}

// ---------------------------------------------------------------------------
// Section composite — cover + content (for use in the full assembled report)
// ---------------------------------------------------------------------------
export function ResponsiblePracticesSection() {
  return (
    <>
      <SectionCover
        bgImage={respPracticesHero}
        accentColor={COLORS.orange}
        title="Responsible Practices"
        subtitle="By fostering a culture of ethics and compliance and investing in supply chain
            initiatives, we aim to integrate responsible practices throughout our business."
        quote="We work to fulfill our mission to connect active people around the world with their passions by creating responsibly made products to help you stay outside longer."
        attribution="— Peter Bragdon, EVP, Chief Administrative Officer and General Counsel"
      />
      <ResponsiblePracticesPage />
    </>
  );
}

// ---------------------------------------------------------------------------
// Standalone Document (used by the generate script)
// ---------------------------------------------------------------------------
export default function ResponsiblePracticesPDF({ pageOffset = 0, totalDocPages = null } = {}) {
  return (
    <PageOffsetContext.Provider value={{ pageOffset, totalDocPages }}>
      <Document
        title="2024 CSC Impact Report — Responsible Practices"
        author="Columbia Sportswear Company"
      >
        <ResponsiblePracticesSection />
      </Document>
    </PageOffsetContext.Provider>
  );
}
